import type { ClassId } from '@xox/shared'
import { ArrowLeft, Dumbbell, Menu as MenuIcon, Play, Swords, Trophy } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { BottomNav, sheetTab } from '../components/BottomNav'
import { BottomSheet } from '../components/BottomSheet'
import { MenuSheet } from '../components/MenuSheet'
import { ProgressCard } from '../components/ProgressCard'
import { ResetCountdown } from '../components/ResetCountdown'
import { Button, Card, LinkButton } from '../components/ui'
import { ClassPicker } from '../dungeon/ClassPicker'
import { runStatusLine } from '../dungeon/copy'
import { DailyBoard } from '../dungeon/DailyBoard'
import { useFetch } from '../hooks/useFetch'
import { api } from '../lib/api'
import { plural } from '../lib/format'
import { useSession } from '../store/session'

type Sheet = 'practice' | 'menu' | null

export function DungeonHome() {
  const me = useSession((s) => s.me)
  const load = useSession((s) => s.load)
  const navigate = useNavigate()
  const { data: today, error } = useFetch(api.dungeonToday)
  const [cls, setCls] = useState<ClassId>('knight')
  const [sheet, setSheet] = useState<Sheet>(null)
  const [starting, setStarting] = useState(false)
  const [startError, setStartError] = useState<string | null>(null)
  useEffect(() => {
    void load()
  }, [load])

  const start = async (kind: 'daily' | 'practice') => {
    setStarting(true)
    setStartError(null)
    try {
      const { run } = await api.startDungeonRun({ kind, cls })
      navigate(`/dungeon/run/${run.id}`)
    } catch (err) {
      setStartError((err as Error).message)
      setStarting(false)
    }
  }
  const unlocked = today?.unlockedClasses ?? me?.progress.unlocked ?? ['knight']

  const practicePanel = (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-slate-500">
        A random dungeon, as many times as you like. Worth a quarter of the XP, capped each day, and
        never on the board.
      </p>
      <ClassPicker value={cls} onChange={setCls} unlocked={unlocked} />
      {today?.practice && (
        <LinkButton to={`/dungeon/run/${today.practice.id}`} variant="secondary" className="w-full">
          Resume practice · floor {today.practice.floor}
        </LinkButton>
      )}
      <Button
        variant="arcade"
        tone="join"
        className="w-full"
        onClick={() => void start('practice')}
        disabled={starting}
      >
        <Dumbbell className="size-5" aria-hidden /> Practice run
      </Button>
    </div>
  )

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-1 flex-col gap-5 py-4">
      <Link
        to="/"
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
      >
        <ArrowLeft className="size-4" aria-hidden /> All games
      </Link>
      {error && <p className="text-rose-600">{error}</p>}

      <section id="daily" aria-labelledby="daily-title" className="scroll-mt-20">
        <Card className="flex flex-col gap-3 bg-[linear-gradient(135deg,var(--poster-dungeon-1),var(--poster-dungeon-2))] text-white ring-0">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest opacity-80">
              Today's dungeon{today ? ` · ${today.day}` : ''}
            </div>
            <h1 id="daily-title" className="text-2xl font-black leading-tight">
              {today?.run ? runStatusLine(today.run) : 'One dungeon. One run. Everyone.'}
            </h1>
            {today && (
              <ResetCountdown
                deadlineAt={today.resetAt}
                serverNow={today.serverNow}
                label="New dungeon in"
                className="text-xs opacity-80"
              />
            )}
          </div>
          {today?.carryOver && (
            <LinkButton
              to={`/dungeon/run/${today.carryOver.id}`}
              variant="secondary"
              className="w-full"
            >
              Finish yesterday's run · floor {today.carryOver.floor}
            </LinkButton>
          )}
          {today && !today.run && (
            <>
              <ClassPicker value={cls} onChange={setCls} unlocked={unlocked} />
              <Button
                variant="arcade"
                tone="go"
                className="w-full"
                onClick={() => void start('daily')}
                disabled={starting}
              >
                <Swords className="size-5" aria-hidden /> Enter the dungeon
              </Button>
            </>
          )}
          {today?.run?.status === 'active' && (
            <LinkButton to={`/dungeon/run/${today.run.id}`} variant="primary" className="w-full">
              <Play className="size-4" aria-hidden /> Resume · floor {today.run.floor}
            </LinkButton>
          )}
          {today?.run && today.run.status !== 'active' && (
            <div className="flex items-center justify-between gap-3 rounded-2xl bg-white/10 px-4 py-3">
              <div>
                <div className="text-2xl font-black tabular-nums">{today.run.score} pts</div>
                <div className="text-xs opacity-80">
                  {today.me ? `#${today.me.rank} today` : 'On the board'} ·{' '}
                  {plural(today.run.kills, 'kill')}
                </div>
              </div>
              <LinkButton to={`/dungeon/run/${today.run.id}`} variant="secondary">
                Review
              </LinkButton>
            </div>
          )}
          {startError && <p className="text-sm text-rose-200">{startError}</p>}
        </Card>
      </section>

      <section className="hidden gap-3 lg:grid lg:grid-cols-2">
        <Card className="flex flex-col gap-3">
          <h2 className="font-semibold">
            <Dumbbell className="mr-1 inline size-4" aria-hidden /> Practice
          </h2>
          {practicePanel}
        </Card>
        {me && <ProgressCard progress={me.progress} />}
      </section>
      <div className="lg:hidden">{me && <ProgressCard progress={me.progress} />}</div>

      <section
        id="board"
        aria-labelledby="board-title"
        className="flex scroll-mt-20 flex-col gap-2"
      >
        <h2 id="board-title" className="text-sm font-bold uppercase tracking-wide text-slate-500">
          <Trophy className="mr-1 inline size-4" aria-hidden /> Today's board
        </h2>
        {today && <DailyBoard entries={today.board} me={me?.playerId ?? null} />}
      </section>

      <BottomNav
        tabs={[
          {
            key: 'daily',
            label: 'Daily',
            icon: <Swords className="size-5" aria-hidden />,
            onSelect: () =>
              document.getElementById('daily')?.scrollIntoView({ behavior: 'smooth' }),
            active: sheet === null,
          },
          sheetTab(
            'practice',
            'Practice',
            <Dumbbell className="size-5" aria-hidden />,
            sheet,
            setSheet,
          ),
          {
            key: 'board',
            label: 'Board',
            icon: <Trophy className="size-5" aria-hidden />,
            onSelect: () =>
              document.getElementById('board')?.scrollIntoView({ behavior: 'smooth' }),
          },
          sheetTab('menu', 'Menu', <MenuIcon className="size-5" aria-hidden />, sheet, setSheet),
        ]}
      />
      <BottomSheet open={sheet === 'practice'} onClose={() => setSheet(null)} title="Practice">
        {practicePanel}
      </BottomSheet>
      <MenuSheet open={sheet === 'menu'} onClose={() => setSheet(null)} current="/dungeon" />
    </div>
  )
}
